import { CommissionStatuses } from 'constants/commissionStatuses';
import { CommissionTypes } from 'constants/commissionTypes';
import { Resources } from 'constants/resources';
import { PAYMENT_ROLES, Role } from 'constants/roles';

import { useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import { Skeleton } from '@mui/material';
import { useCommissionsAvailableActions } from 'hooks/queries/useCommissionsAvailableActions';
import { usePermissions } from 'hooks/usePermissions';
import { NewVersionCommission } from 'pages/Merchants/MerchantsShow/MerchantShowCommissions/MerchantShowCommissionsCreateNewVersionDialog/MerchantShowCommissionsCreateNewVersionDialog';
import { MerchantShowCommissionsFormValues } from 'pages/Merchants/MerchantsShow/MerchantShowCommissions/MerchantShowCommissionsList/MerchantShowCommissionsList';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RecordContextProvider, useGetOne } from 'react-admin';
import { UseFormSetValue } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { Button } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import {
  ContextMenu,
  ContextMenuButton,
  ContextMenuDeleteButton,
  ContextMenuEntityUserActionsButton,
} from 'shared/mui/ContextMenu';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { UserActionsIconButton } from 'shared/mui/IconButtons/UserActionsIconButton/UserActionsIconButton';
import { FunctionField } from 'shared/react-admin/Fields';
import { CommissionsShowChipFields } from 'shared/widgets/CommissionsShowChipFields/CommissionsShowChipFields';
import { CommissionsTabs } from 'shared/widgets/CommissionsTabs/CommissionsTabs';
import { CommissionsNestedDialogsProvider } from 'shared/widgets/CommissionsTabs/context/CommissionsNestedDialogsContext';
import { Commission } from 'types/embedded/commission';
import { Permission } from 'types/embedded/permission';
import { getId } from 'utils';

import { MerchantShowCommissionRelatedCommissionsLink } from './components/MerchantShowCommissionRelatedCommissionsLink';
import { MerchantShowCommissionsShowDialogAlert } from './MerchantShowCommissionsShowDialogAlert';
import { CommissionsNewVersionButton } from '../../../../Commissions/components/CommissionsNewVersionButton';

type Props = {
  open: boolean;
  onEdit: (record: Commission) => void;
  onDelete: (record: Commission) => void;
  handleOpenModalImplementError: (record: Commission) => void;
  handleOpenModalImplement: (record: Commission) => void;
  handleOpenModalNewVersion: (record: NewVersionCommission) => void;
  handleOpenModalArchivedError: (record: Commission) => void;
  handleOpenModalAgreement: (record: Commission) => void;
  handleOpenModalAgree: (record: Commission) => void;
  handleOpenModalReject: (record: Commission) => void;
  handleOpenModalCancel: (record: Commission) => void;
  handleOpenModalArchived: (record: Commission) => void;
  onClose: () => void;
  record?: Commission;
  setAppliedFilters: (value: Partial<MerchantShowCommissionsFormValues>) => void;
  setValue: UseFormSetValue<MerchantShowCommissionsFormValues>;
};

export const MerchantShowCommissionsShowDialog: React.FC<Props> = ({
  open,
  onClose,
  onEdit,
  onDelete,
  handleOpenModalImplementError,
  handleOpenModalImplement,
  handleOpenModalNewVersion,
  handleOpenModalArchivedError,
  handleOpenModalAgreement,
  handleOpenModalAgree,
  handleOpenModalReject,
  handleOpenModalCancel,
  handleOpenModalArchived,
  record: recordProp,
  setAppliedFilters,
  setValue,
}) => {
  const translate = useTypedTranslate();

  const { hasResourceAccess, hasResourcePermissions, roles } = usePermissions();

  const { data: updatedRecord } = useGetOne<Commission>(
    Resources.Commissions,
    { id: recordProp?.['@id'] },
    { enabled: open && Boolean(recordProp?.['@id']) },
  );
  const record = updatedRecord ?? recordProp;

  const [isNestedDialogOpen, setIsNestedDialogOpen] = useState(false);

  const { data = [], isLoading } = useCommissionsAvailableActions(record?.originId);

  const isFinanceRoleWithoutRecalculationTasks =
    !record?.recalculationTasks?.length &&
    (roles.includes(Role.Finance) || roles.includes(Role.FinanceLead));

  const renderActions = (): React.ReactElement | React.ReactElement[] | false => {
    if (isLoading) {
      return <Skeleton height={36} variant="rounded" width={240} />;
    }

    const actions = [];

    if (
      !data.includes(CommissionStatuses.Implemented) &&
      !data.includes(CommissionStatuses.Archived) &&
      record?.status?.value === CommissionStatuses.Implemented &&
      (roles.includes(Role.Admin) || roles.includes(Role.Tech) || roles.includes(Role.Payment))
    ) {
      actions.push(
        <Button
          color="error"
          key="btn-cancel"
          label={translate('app.buttons.archived')}
          onClick={() => record && handleOpenModalArchivedError(record)}
          variant="outlined"
        />,
      );
    }

    if (
      !data.includes(CommissionStatuses.Archived) &&
      ![
        CommissionStatuses.Created,
        CommissionStatuses.OnApproval,
        CommissionStatuses.Rejected,
        CommissionStatuses.Approved,
        CommissionStatuses.Cancelled,
        CommissionStatuses.Implemented,
        CommissionStatuses.Archived,
      ].includes(record?.status?.value as CommissionStatuses) &&
      roles.includes(Role.Payment)
    ) {
      actions.push(
        <Button
          color="error"
          key="btn-cancel"
          label={translate('app.buttons.archived')}
          onClick={() => record && handleOpenModalArchivedError(record)}
          variant="outlined"
        />,
      );
    }

    if (data.includes(CommissionStatuses.Cancelled)) {
      actions.push(
        <Button
          color="error"
          key="btn-cancel"
          label={translate('app.buttons.annul')}
          onClick={() => record && handleOpenModalCancel(record)}
          variant="outlined"
        />,
      );
    }

    if (data.includes(CommissionStatuses.Archived)) {
      actions.push(
        <Button
          color="error"
          key="btn-cancel"
          label={translate('app.buttons.archived')}
          onClick={() => {
            record && handleOpenModalArchived(record);
          }}
          variant="outlined"
        />,
      );
    }

    if (data.includes(CommissionStatuses.Rejected)) {
      actions.push(
        <Button
          color="error"
          key="btn-reject"
          label={translate('app.buttons.reject')}
          onClick={() => record && handleOpenModalReject(record)}
          variant="outlined"
        />,
      );
    }

    if (
      !data.includes(CommissionStatuses.Implemented) &&
      record?.status?.value === CommissionStatuses.Approved &&
      (roles.includes(Role.Admin) || roles.includes(Role.Tech))
    ) {
      actions.push(
        <Button
          color="primary"
          key="btn-implement-ex"
          label={translate('app.buttons.activate')}
          onClick={() => record && handleOpenModalImplementError(record)}
          variant="contained"
        />,
      );
    }

    if (
      !data.includes(CommissionStatuses.Implemented) &&
      record?.status?.value === CommissionStatuses.Approved &&
      isFinanceRoleWithoutRecalculationTasks
    ) {
      actions.push(
        <Button
          color="primary"
          key="btn-implement-ex"
          label={translate('app.buttons.activate')}
          onClick={() => record && handleOpenModalImplementError(record)}
          variant="contained"
        />,
      );
    }

    if (data.includes(CommissionStatuses.CreateChildAvailable)) {
      actions.push(
        <CommissionsNewVersionButton
          key="btn-new-version"
          onClick={(e) => {
            e.preventDefault();
            record && handleOpenModalNewVersion({ ...record, parent: record?.id });
          }}
          pathname="/commissions/create-new"
        />,
      );
    }

    if (
      !data.includes(CommissionStatuses.Implemented) &&
      ![
        CommissionStatuses.Created,
        CommissionStatuses.OnApproval,
        CommissionStatuses.Rejected,
        CommissionStatuses.Cancelled,
        CommissionStatuses.Implemented,
        CommissionStatuses.Archived,
      ].includes(record?.status?.value as CommissionStatuses) &&
      roles.includes(Role.Payment)
    ) {
      actions.push(
        <Button
          color="primary"
          key="btn-implement-ex"
          label={translate('app.buttons.activate')}
          onClick={() => record && handleOpenModalImplementError(record)}
          variant="contained"
        />,
      );
    }

    if (data.includes(CommissionStatuses.OnApproval)) {
      actions.push(
        <Button
          color="primary"
          key="btn-agreement"
          label={translate('app.buttons.approval')}
          onClick={() => record && handleOpenModalAgreement(record)}
          variant="contained"
        />,
      );
    }

    if (data.includes(CommissionStatuses.Approved)) {
      actions.push(
        <Button
          color="primary"
          key="btn-agree"
          label={translate('app.buttons.approved')}
          onClick={() => record && handleOpenModalAgree(record)}
          variant="contained"
        />,
      );
    }

    if (data.includes(CommissionStatuses.Implemented)) {
      actions.push(
        <Button
          color="primary"
          key="btn-implement"
          label={translate('app.buttons.activate')}
          onClick={() => record && handleOpenModalImplement(record)}
          variant="contained"
        />,
      );
    }

    return actions;
  };

  const isShowButton = record?.status
    ? [CommissionStatuses.Created, CommissionStatuses.Rejected].includes(
        record?.status?.value as CommissionStatuses,
      )
    : false;

  const showApprovedAlert =
    ![...PAYMENT_ROLES, Role.Admin].some((role) => roles.includes(role)) &&
    Boolean(record?.status) &&
    (record?.status?.value as CommissionStatuses) === CommissionStatuses.Approved &&
    !isFinanceRoleWithoutRecalculationTasks;

  const showImplementedAlert =
    ![...PAYMENT_ROLES, Role.Admin].some((role) => roles.includes(role)) &&
    Boolean(record?.status) &&
    (record?.status?.value as CommissionStatuses) === CommissionStatuses.Implemented;
  const actions = renderActions();

  // @TODO - посмотреть как сделана ссылка на странице комиссий
  const onClickRelatedCommission = () => {
    switch (record?.typeEnum?.value) {
      case CommissionTypes.Child:
        setAppliedFilters({ id: getId(record?.parent?.['@id']) });
        setValue('id', getId(record?.parent?.['@id']));
        setValue('parent.id', null);
        onClose();
        break;
      case CommissionTypes.Parent:
        setAppliedFilters({ parent: { id: String(record?.originId) } });
        setValue('parent.id', String(record?.originId));
        setValue('id', '');
        onClose();
        break;
      default:
        break;
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      onClose={onClose}
      open={open}
      sx={{ display: isNestedDialogOpen ? 'none' : undefined }}
    >
      <RecordContextProvider value={record}>
        <CardHeader
          appendActions={[
            !isShowButton && hasResourceAccess(Resources.UserActions) && (
              <UserActionsIconButton entityName="commission" key="user-action-link" />
            ),
            isShowButton && (
              <ContextMenu key="menu-button">
                {hasResourceAccess(Resources.UserActions) && (
                  <ContextMenuEntityUserActionsButton
                    entityName="commission"
                    key="user-action-link"
                  />
                )}
                {hasResourcePermissions(Resources.Commissions, [
                  Permission.Full,
                  Permission.Update,
                ]) && (
                  <ContextMenuButton
                    icon={<EditIcon />}
                    label={translate('app.buttons.edit')}
                    onClick={() => {
                      onClose();
                      record && onEdit(record);
                    }}
                  />
                )}
                {hasResourcePermissions(Resources.Commissions, [
                  Permission.Full,
                  Permission.Delete,
                ]) && (
                  <ContextMenuDeleteButton
                    onClick={() => {
                      onClose();
                      record && onDelete(record);
                    }}
                  />
                )}
              </ContextMenu>
            ),
            <CloseIconButton key="close-button" onClose={onClose} />,
          ]}
          title={translate('app.commissions.details')}
          titleSize="smallBold"
        />
        <Box
          sx={{
            maxHeight: '650px',
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '24px',
          }}
        >
          <CommissionsShowChipFields />
          <CommissionsNestedDialogsProvider setNestedDialogOpen={setIsNestedDialogOpen}>
            <CommissionsTabs
              omit="provider"
              relatedField={
                <FunctionField
                  render={(record: Commission) => (
                    <MerchantShowCommissionRelatedCommissionsLink
                      onClick={onClickRelatedCommission}
                      record={record}
                    />
                  )}
                />
              }
            />
          </CommissionsNestedDialogsProvider>
        </Box>
        {showApprovedAlert && (
          <MerchantShowCommissionsShowDialogAlert>
            Для внедрения комиссии обратитесь к администратору
          </MerchantShowCommissionsShowDialogAlert>
        )}
        {showImplementedAlert && (
          <MerchantShowCommissionsShowDialogAlert>
            Для архивирования данной комиссии обратитесь к администратору или создайте и внедрите
            новую дочернюю комиссию.
          </MerchantShowCommissionsShowDialogAlert>
        )}
        <CardActionsButtons rightActionsSlot={actions} sx={{ paddingTop: 0, minHeight: '60px' }} />
      </RecordContextProvider>
    </Dialog>
  );
};
